import { useBloc } from "@lib/bloc/hooks"
import { LoadingIndicator, LoadingIndicatorContainer } from "@ui/common/LoadingIndicator"
import { Page } from "@ui/common/page/Page"
import { PageToolbar, PageToolbarTitle } from "@ui/common/page/PageToolbar"
import { DI } from "@ui/DI"
import React, { useEffect } from "react"
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts"
import type { PagePerformanceRow, TimeSeriesPoint } from "@service/performance/PerformanceService"

export function PerformanceScreen(): JSX.Element {
    const [state, bloc] = useBloc(() => DI.performanceBloc())

    useEffect(() => {
        bloc.loadDashboard()
    }, [])

    if (!state.isConfigured) {
        return (
            <Page className="page--performance">
                <PageToolbar>
                    <PageToolbarTitle>Gauge Performance Dashboard</PageToolbarTitle>
                </PageToolbar>
                <div className="perf-dashboard">
                    <div className="perf-empty-state">
                        Performance monitoring is not yet configured for this environment.
                    </div>
                </div>
            </Page>
        )
    }

    return (
        <Page className="page--performance">
            <PageToolbar>
                <PageToolbarTitle>Gauge Performance Dashboard</PageToolbarTitle>
            </PageToolbar>
            <div className="perf-dashboard">
                <LoadingIndicatorContainer>
                    <LoadingIndicator isLoading={state.isLoading} />

                    {/* ── Summary Row ── */}
                    <div className="perf-summary-row">
                        <MetricCard
                            label="Typical Load Time"
                            value={formatDuration(state.overview?.avgPageLoadMs ?? null)}
                            subtitle="Last 24 hours"
                        />
                        <MetricCard
                            label="95% of Pages Load Within"
                            value={formatDuration(state.overview?.p95PageLoadMs ?? null)}
                            subtitle="Last 24 hours"
                        />
                        <MetricCard
                            label="Page Views"
                            value={state.overview?.totalPageViews?.toLocaleString() ?? "—"}
                            subtitle="Last 24 hours"
                        />
                    </div>

                    {/* ── Trend Chart ── */}
                    <div className="perf-chart-card perf-chart-card--full">
                        <div className="perf-chart-header">
                            <h3 className="perf-chart-title">Last 30 Days</h3>
                            <span className="perf-chart-subtitle">Daily avg</span>
                        </div>
                        <div className="perf-chart-body">
                            {state.last30DaysTrend && state.last30DaysTrend.points.length > 0 ? (
                                <TrendChart
                                    points={state.last30DaysTrend.points}
                                    formatLabel={formatDayLabel}
                                    color="#7BC131"
                                    gradientId="g30d"
                                />
                            ) : !state.isLoading ? (
                                <div className="perf-empty-state">No data for this period</div>
                            ) : null}
                        </div>
                    </div>

                    {/* ── Per-Page Breakdown ── */}
                    {state.perPageMetrics.length > 0 && (
                        <div className="perf-page-section">
                            <div className="perf-page-section-header">
                                <h3 className="perf-chart-title">Page Performance</h3>
                                <span className="perf-chart-subtitle">Last 30 days</span>
                            </div>
                            <div className="perf-page-grid">
                                <div className="perf-page-table-card">
                                    <PageTable rows={state.perPageMetrics} />
                                </div>
                                <div className="perf-page-chart-card">
                                    <PageBarChart rows={state.perPageMetrics} />
                                </div>
                            </div>
                        </div>
                    )}

                    {state.error && !state.isLoading && <div className="perf-error-banner">{state.error}</div>}
                </LoadingIndicatorContainer>
            </div>
        </Page>
    )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MetricCard({ label, value, subtitle }: { label: string; value: string; subtitle: string }): JSX.Element {
    return (
        <div className="perf-metric-card">
            <div className="perf-metric-label">{label}</div>
            <div className="perf-metric-value">{value}</div>
            <div className="perf-metric-subtitle">{subtitle}</div>
        </div>
    )
}

function TrendChart({
    points,
    formatLabel,
    color,
    gradientId,
}: {
    points: TimeSeriesPoint[]
    formatLabel: (ts: string) => string
    color: string
    gradientId: string
}): JSX.Element {
    const chartData = points.map((p) => ({
        label: formatLabel(p.timestamp),
        seconds: parseFloat((p.avgMs / 1000).toFixed(2)),
    }))

    return (
        <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                <defs>
                    <linearGradient id={gradientId} x1={0} x2={0} y1={0} y2={1}>
                        <stop offset="0%" stopColor={color} stopOpacity={0.15} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid horizontal vertical={false} strokeWidth={1} stroke="#E8EAED" />
                <XAxis
                    dataKey="label"
                    fontSize={11}
                    axisLine={false}
                    tickLine={false}
                    stroke="#80868b"
                    interval="preserveStartEnd"
                    minTickGap={50}
                />
                <YAxis
                    fontSize={11}
                    axisLine={false}
                    tickLine={false}
                    stroke="#80868b"
                    width={40}
                    tickFormatter={(v: number) => `${v}s`}
                />
                <Tooltip
                    formatter={(value: number) => [`${value}s`, "Load Time"]}
                    contentStyle={{
                        background: "#fff",
                        border: "1px solid #dadce0",
                        borderRadius: 6,
                        fontSize: 13,
                        boxShadow: "0 1px 4px rgba(0,0,0,.08)",
                    }}
                />
                <Area
                    type="monotone"
                    dataKey="seconds"
                    stroke={color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: color, strokeWidth: 0 }}
                    fill={`url(#${gradientId})`}
                    isAnimationActive={false}
                />
            </AreaChart>
        </ResponsiveContainer>
    )
}

function PageTable({ rows }: { rows: PagePerformanceRow[] }): JSX.Element {
    return (
        <table className="perf-table">
            <thead>
                <tr>
                    <th>Page</th>
                    <th>Views</th>
                    <th>Avg Load</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.pageName}>
                        <td>{r.pageName}</td>
                        <td className="perf-table-num">{r.views.toLocaleString()}</td>
                        <td className="perf-table-num">{formatDuration(r.avgLoadMs)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

const BAR_COLORS = ["#4A90D9", "#7BC131", "#F4B824", "#E8563A", "#9B59B6", "#1ABC9C", "#E67E22"]

function PageBarChart({ rows }: { rows: PagePerformanceRow[] }): JSX.Element {
    const data = rows.map((r) => ({
        name: r.pageName,
        seconds: parseFloat((r.avgLoadMs / 1000).toFixed(2)),
    }))

    return (
        <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 44)}>
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 4 }}>
                <CartesianGrid horizontal={false} vertical strokeWidth={1} stroke="#E8EAED" />
                <XAxis
                    type="number"
                    fontSize={11}
                    axisLine={false}
                    tickLine={false}
                    stroke="#80868b"
                    tickFormatter={(v: number) => `${v}s`}
                />
                <YAxis
                    type="category"
                    dataKey="name"
                    fontSize={12}
                    axisLine={false}
                    tickLine={false}
                    stroke="#202124"
                    width={120}
                />
                <Tooltip
                    formatter={(value: number) => [`${value}s`, "Avg Load"]}
                    contentStyle={{
                        background: "#fff",
                        border: "1px solid #dadce0",
                        borderRadius: 6,
                        fontSize: 13,
                        boxShadow: "0 1px 4px rgba(0,0,0,.08)",
                    }}
                />
                <Bar dataKey="seconds" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                    {data.map((_, i) => (
                        <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    )
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDuration(ms: number | null): string {
    if (ms === null || ms === undefined) return "—"
    if (ms === 0) return "0ms"
    if (ms < 1000) return `${Math.round(ms)}ms`
    return `${(ms / 1000).toFixed(1)}s`
}

function formatHourLabel(isoTimestamp: string): string {
    try {
        const d = new Date(isoTimestamp)
        return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    } catch {
        return ""
    }
}

function formatDayLabel(isoTimestamp: string): string {
    try {
        const d = new Date(isoTimestamp)
        return d.toLocaleDateString([], { month: "short", day: "numeric" })
    } catch {
        return ""
    }
}
